import React from 'react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LineChart,
  Line,
  Legend,
  LabelList,
  ReferenceLine,
} from 'recharts';

function formatDisplayUnit(unit) {
  return /^pp$/i.test((unit || '').trim()) ? '% points' : (unit || '');
}

// ─── Pareto Chart ─────────────────────────────────────────────────────────────
//
// Two modes based on data content:
//
// 1. STANDARD (PPH or all-positive gap values):
//    Regular left-aligned bars sorted worst-first at top.
//
// 2. DIVERGING (gap data that contains any negative values — i.e. below-target):
//    Zero axis centered. Negative miss values → blue bars right.
//    Positive above-target values → green bars left.
//    This communicates both direction and magnitude in one glance.
//
function ParetoChart({ title, data }) {
  if (!data || data.length === 0) return null;

  // Detect whether this is a gap chart with mixed signs (diverging mode)
  const isGapUnit = data.some(d => /\bpp\b|gap|%/i.test(d.unit || ''));
  const hasMixedSigns = data.some(d => d.value < 0) && data.some(d => d.value > 0);
  const hasSomePctSign = isGapUnit && data.some(d => d.value < 0);
  const isDivergingMode = isGapUnit && (hasMixedSigns || hasSomePctSign);

  // ── DIVERGING MODE ──────────────────────────────────────────────────────────
  if (isDivergingMode) {
    // Sort ascending (most negative = worst below-target first).
    // recharts layout="vertical" renders index-0 at the TOP, so worst appears first.
    const sorted = [...data].sort((a, b) => a.value - b.value);
    const chartData = sorted.map(d => ({
      label: d.label,
      unit: formatDisplayUnit(d.unit),
      // Below-target misses are shown to the right in the same blue used by standard charts.
      miss: d.value < 0 ? Math.abs(d.value) : 0,
      gain: d.value > 0 ? -d.value : 0,
      rawValue: d.value,
    }));

    const maxAbs = Math.max(...data.map(d => Math.abs(d.value)), 1);
    const unit = formatDisplayUnit(data[0]?.unit);
    const formatSigned = (value) => {
      if (!Number.isFinite(value)) return '';
      const rounded = Math.round(value * 10) / 10;
      const sign = rounded > 0 ? '+' : '';
      return `${sign}${rounded}`;
    };

    const DivergingTooltip = ({ active, payload }) => {
      if (!active || !payload || !payload.length) return null;
      const d = payload[0]?.payload;
      if (!d) return null;
      const color = d.rawValue < 0 ? '#2563eb' : d.rawValue > 0 ? '#22c55e' : '#94a3b8';
      const label = d.rawValue < 0 ? 'below target' : d.rawValue > 0 ? 'above target' : 'on target';
      return (
        <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: '6px', padding: '8px 12px', fontSize: '13px', boxShadow: '0 2px 8px rgba(0,0,0,0.08)' }}>
          <div style={{ fontWeight: 600 }}>{d.label}</div>
          <div style={{ color }}>{formatSigned(d.rawValue)} {unit} — {label}</div>
        </div>
      );
    };

    const renderDivLabel = (props) => {
      const { x, y, width, height, value, index } = props;
      if (!isFinite(x) || !isFinite(y) || !isFinite(width) || !isFinite(height)) return null;
      if (value === 0 || !isFinite(value)) return null;
      const item = chartData[index] || {};
      // Use value sign (not rawValue) to determine bar direction:
      //   miss bars have value > 0  → bar extends RIGHT  → label goes right of bar tip
      //   gain bars have value < 0  → bar extends LEFT   → label goes left of bar tip
      // Math.max/min handles recharts giving either positive or negative 'width' for leftward bars.
      const isMissBar = value > 0;
      const barRight = x + Math.max(0, width);
      const barLeft  = x + Math.min(0, width);
      const labelX   = isMissBar ? barRight + 8 : barLeft - 8;
      const anchor   = isMissBar ? 'start' : 'end';
      return (
        <text x={labelX} y={y + height / 2 + 4} fontSize={11} fontWeight="600" fill={isMissBar ? '#2563eb' : '#22c55e'} textAnchor={anchor}>
          {formatSigned(item.rawValue)} {item.unit}
        </text>
      );
    };

    return (
      <div className="chart-wrapper" style={{ margin: '12px 0', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '700px' }}>
        {title && <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '4px', color: '#1e293b' }}>{title}</div>}
        <div style={{ fontSize: '11px', color: '#64748b', marginBottom: '10px' }}>
          <span style={{ color: '#2563eb', fontWeight: 600 }}>■</span> Below target &nbsp;
          <span style={{ color: '#22c55e', fontWeight: 600 }}>■</span> Above target
        </div>
        <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 40)}>
          <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 110, bottom: 0, left: 110 }}
            stackOffset="sign">
            <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
            <ReferenceLine x={0} stroke="#94a3b8" strokeDasharray="2 2" />
            <XAxis
              type="number"
              domain={[-maxAbs * 1.15, maxAbs * 1.15]}
              tick={{ fontSize: 11 }}
              axisLine={false}
              tickLine={false}
              tickFormatter={v => v === 0 ? '0' : ''}
            />
            <YAxis type="category" dataKey="label" width={150} tick={{ fontSize: 12, fill: '#374151' }} axisLine={false} tickLine={false} />
            <Tooltip content={<DivergingTooltip />} cursor={{ fill: '#f0f4ff' }} />
            {/* Below-target miss (blue, rightward) */}
            <Bar dataKey="miss" stackId="diverge" fill="#2563eb" radius={[0, 4, 4, 0]} barSize={22}>
              <LabelList content={renderDivLabel} />
            </Bar>
            {/* Above-target gain (green, leftward) — radius=[0,4,4,0] rounds the outer left tip */}
            <Bar dataKey="gain" stackId="diverge" fill="#22c55e" radius={[0, 4, 4, 0]} barSize={22}>
              <LabelList content={renderDivLabel} />
            </Bar>
          </BarChart>
        </ResponsiveContainer>
      </div>
    );
  }

  // ── STANDARD MODE ───────────────────────────────────────────────────────────
  // Data arrives pre-sorted from _buildParetoData:
  //   • PPH rows: ascending (lowest = bottleneck at top, worst machine first)
  //   • Gap rows (all-positive): descending absolute value (largest bar first)
  // recharts layout="vertical" renders index-0 at TOP — so we use data as-is,
  // no reversal needed (previously .reverse() caused biggest bar to appear at bottom).
  const chartData = [...data].map(d => ({
    label: d.label,
    value: Math.abs(d.value),
    unit: formatDisplayUnit(d.unit),
  }));

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;
    const d = payload[0].payload;
    return (
      <div style={{
        background: '#fff',
        border: '1px solid #e2e8f0',
        borderRadius: '6px',
        padding: '8px 12px',
        fontSize: '13px',
        boxShadow: '0 2px 8px rgba(0,0,0,0.08)'
      }}>
        <div style={{ fontWeight: 600 }}>{d.label}</div>
        <div style={{ color: '#2563eb' }}>{d.value} {d.unit}</div>
      </div>
    );
  };

  // LabelList content renderer with NaN guard
  const renderBarLabel = (props) => {
    const { x, y, width, height, value, index } = props;
    if (!isFinite(x) || !isFinite(y) || !isFinite(width) || !isFinite(height)) return null;
    const item = chartData[index] || {};
    return (
      <text x={x + width + 6} y={y + height / 2 + 4} fontSize={11} fill="#555">
        {value} {item.unit}
      </text>
    );
  };

  return (
    <div className="chart-wrapper" style={{ margin: '12px 0', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '580px' }}>
      {title && <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '12px', color: '#1e293b' }}>{title}</div>}
      <ResponsiveContainer width="100%" height={Math.max(160, chartData.length * 40)}>
        <BarChart data={chartData} layout="vertical" margin={{ top: 0, right: 110, bottom: 0, left: 8 }}>
          <CartesianGrid strokeDasharray="3 3" horizontal={false} stroke="#e2e8f0" />
          <XAxis type="number" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
          <YAxis
            type="category"
            dataKey="label"
            width={120}
            tick={{ fontSize: 12, fill: '#374151' }}
            axisLine={false}
            tickLine={false}
          />
          <Tooltip content={<CustomTooltip />} cursor={{ fill: '#f0f4ff' }} />
          <Bar dataKey="value" fill="#2563eb" radius={[0, 4, 4, 0]} barSize={22}>
            <LabelList content={renderBarLabel} />
          </Bar>
        </BarChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Trend Chart ──────────────────────────────────────────────────────────────
function TrendChart({ title, data, actualLabel, goalLabel, yAxisLabel, xAxisLabel }) {
  if (!data || data.length === 0) return null;

  const chartData = data.map(d => ({
    x: d.x,
    actual: d.actual,
    goal: d.goal,
  }));

  // Zoom the Y-axis to the data range so the gap between actual and target is visible.
  // Without this, a 0–120% scale makes a 9pp gap look like a flat line.
  const allValues = chartData.flatMap(d => [d.actual, d.goal]).filter(v => v !== null && v !== undefined && isFinite(v));
  const dataMin = allValues.length > 0 ? Math.min(...allValues) : 0;
  const dataMax = allValues.length > 0 ? Math.max(...allValues) : 100;
  const pad = Math.max((dataMax - dataMin) * 0.15, 3); // at least 3 units of padding
  const yMin = Math.floor(dataMin - pad);
  const yMax = Math.ceil(dataMax + pad);

  // Format ISO date strings (2025-06-01) as day numbers (1) for x-axis ticks
  const formatXTick = (val) => {
    if (typeof val === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(val)) {
      return parseInt(val.split('-')[2], 10).toString();
    }
    return val;
  };

  // Sample x-axis ticks: show at most 10 evenly-spaced ticks to avoid crowding.
  const step = Math.max(1, Math.ceil(chartData.length / 10));
  const sampledTicks = chartData
    .filter((_, i) => i % step === 0 || i === chartData.length - 1)
    .map(d => d.x);

  const xAxisBottomPad = xAxisLabel ? 36 : 20;

  return (
    <div className="chart-wrapper" style={{ margin: '12px 0', padding: '16px', background: '#f8fafc', borderRadius: '8px', border: '1px solid #e2e8f0', maxWidth: '580px' }}>
      {title && <div style={{ fontWeight: 600, fontSize: '13px', marginBottom: '12px', color: '#1e293b' }}>{title}</div>}
      <ResponsiveContainer width="100%" height={240}>
        <LineChart data={chartData} margin={{ top: 4, right: 20, bottom: xAxisLabel ? 36 : 16, left: yAxisLabel ? 40 : 16 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            dataKey="x"
            tick={{ fontSize: 11 }}
            tickFormatter={formatXTick}
            ticks={sampledTicks}
            height={xAxisBottomPad}
            label={xAxisLabel ? { value: xAxisLabel, position: 'insideBottom', offset: -8, fontSize: 11, fill: '#64748b' } : undefined}
          />
          <YAxis
            domain={[yMin, yMax]}
            width={yAxisLabel ? 60 : 40}
            tick={{ fontSize: 11 }}
            label={yAxisLabel ? { value: yAxisLabel, angle: -90, position: 'insideLeft', offset: 14, fontSize: 11, fill: '#64748b' } : undefined}
          />
          <Tooltip
            contentStyle={{ fontSize: '12px', borderRadius: '6px' }}
            formatter={(val) => val !== null ? val : '—'}
          />
          <Legend verticalAlign="top" wrapperStyle={{ fontSize: '12px', paddingBottom: '8px' }} />
          {chartData.some(d => d.actual !== null) && (
            <Line
              type="monotone"
              dataKey="actual"
              name={actualLabel || 'Actual'}
              stroke="#2563eb"
              strokeWidth={2}
              dot={{ r: 3 }}
              connectNulls
            />
          )}
          {chartData.some(d => d.goal !== null) && (
            <Line
              type="monotone"
              dataKey="goal"
              name={goalLabel || 'Goal'}
              stroke="#ef4444"
              strokeWidth={2}
              strokeDasharray="6 4"
              dot={false}
              connectNulls
            />
          )}
        </LineChart>
      </ResponsiveContainer>
    </div>
  );
}

// ─── Dispatcher ───────────────────────────────────────────────────────────────
export default function StructuredChart({ chart }) {
  if (!chart) return null;
  switch (chart.type) {
    case 'pareto':
      return <ParetoChart title={chart.title} data={chart.data} />;
    case 'trend':
      return <TrendChart title={chart.title} data={chart.data} actualLabel={chart.actualLabel} goalLabel={chart.goalLabel} yAxisLabel={chart.yAxisLabel} xAxisLabel={chart.xAxisLabel} />;
    default:
      return null;
  }
}
